import type { SupportedStorage } from '@supabase/auth-js'
import { logAuthEvent } from './authDiag'

// Hybrid storage adapter for GoTrueClient. localStorage is the primary
// (fast, synchronous, well-tested) store; IndexedDB is a durable mirror.
//
// Why this exists — the recurring "Safari mobile signed me out again"
// reports. iOS Safari evicts localStorage in two scenarios that hit
// TrybeUP users daily:
//
//   1. Memory-pressure eviction. When iOS reclaims memory from
//      backgrounded tabs (phone sleep, app switching, low-RAM
//      conditions), it sometimes wipes the page's localStorage before
//      killing the tab — and the next reload finds the GoTrue session
//      key gone. Three earlier rounds of fixes (provider-gate, idle
//      heartbeat, warmer retry) all targeted refresh-failure paths, but
//      none could help here: when the session is wiped, there is no
//      token left to refresh.
//
//   2. Intelligent Tracking Prevention (ITP) 7-day cleanup. First-party
//      storage on sites the user hasn't actively interacted with in a
//      week gets purged. Daily users escape this, but anyone returning
//      after a long gap loses their session.
//
// IndexedDB is governed by different — and more lenient — eviction
// rules: it survives memory-pressure tab kills and only enters ITP
// cleanup after a longer no-interaction window. Mirroring the session
// there means the next page load can restore it into localStorage,
// invisibly to the user.
//
// Backwards-compat: existing users who only have a session in
// localStorage get it lazily mirrored into IDB on the first read after
// this lands, so the very next Safari wipe doesn't catch them out.

const DB_NAME = 'trybeup_auth'
const STORE = 'kv'
const DB_VERSION = 1
// Cap how long we'll wait on an IDB open. Safari in private mode can
// hang the open() call indefinitely; falling back to localStorage-only
// after 2s keeps the auth flow responsive in that degenerate case.
const IDB_OPEN_TIMEOUT_MS = 2000

let dbPromise: Promise<IDBDatabase | null> | null = null

function openDB(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null)
      return
    }
    let settled = false
    const finish = (db: IDBDatabase | null) => {
      if (settled) return
      settled = true
      resolve(db)
    }
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION)
      req.onerror = () => finish(null)
      req.onblocked = () => finish(null)
      req.onupgradeneeded = () => {
        try {
          if (!req.result.objectStoreNames.contains(STORE)) {
            req.result.createObjectStore(STORE)
          }
        } catch {
          /* ignore — upgrade failure means we fall back to localStorage */
        }
      }
      req.onsuccess = () => finish(req.result)
      setTimeout(() => finish(null), IDB_OPEN_TIMEOUT_MS)
    } catch {
      finish(null)
    }
  })
  return dbPromise
}

async function idbGet(key: string): Promise<string | null> {
  const db = await openDB()
  if (!db) return null
  return new Promise<string | null>((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readonly')
      const store = tx.objectStore(STORE)
      const req = store.get(key)
      req.onerror = () => resolve(null)
      req.onsuccess = () => {
        const value = req.result
        resolve(typeof value === 'string' ? value : null)
      }
    } catch {
      resolve(null)
    }
  })
}

async function idbSet(key: string, value: string): Promise<'ok' | 'no-db' | 'tx-threw' | 'req-error'> {
  const db = await openDB()
  if (!db) return 'no-db'
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite')
      const store = tx.objectStore(STORE)
      const req = store.put(value, key)
      req.onerror = () => resolve('req-error')
      req.onsuccess = () => resolve('ok')
    } catch {
      resolve('tx-threw')
    }
  })
}

async function idbRemove(key: string): Promise<'ok' | 'no-db' | 'tx-threw' | 'req-error'> {
  const db = await openDB()
  if (!db) return 'no-db'
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite')
      const store = tx.objectStore(STORE)
      const req = store.delete(key)
      req.onerror = () => resolve('req-error')
      req.onsuccess = () => resolve('ok')
    } catch {
      resolve('tx-threw')
    }
  })
}

// Session-expiry tombstone. Written on every successful session-bearing
// getItem() and on every setItem() that carries a session — gives the
// /login diagnostic an independent record of "the session was last seen
// alive at X, supposed to expire at Y". Survives a session wipe (it's a
// separate localStorage key the GoTrue SDK doesn't touch), so on /login
// we can tell "Safari killed the session N minutes before it would have
// expired naturally".
const TOMBSTONE_KEY = 'trybeup_session_tombstone'

interface SessionTombstone {
  /** ISO timestamp of the most recent session sighting. */
  last_seen_at: string
  /** Session expiry as unix-seconds (matches GoTrue's expires_at field). */
  expires_at: number | null
  /** Was this written from getItem (read) or setItem (write)? */
  source: 'read' | 'write'
}

function writeTombstone(value: string, source: 'read' | 'write') {
  try {
    let expires_at: number | null = null
    try {
      const parsed = JSON.parse(value)
      const ea = parsed?.expires_at
      if (typeof ea === 'number' && Number.isFinite(ea)) expires_at = ea
    } catch { /* not parseable, leave expires_at null */ }
    const t: SessionTombstone = {
      last_seen_at: new Date().toISOString(),
      expires_at,
      source,
    }
    localStorage.setItem(TOMBSTONE_KEY, JSON.stringify(t))
  } catch { /* never throw from a diagnostic */ }
}

// We only instrument the GoTrue session key — that's the one we care
// about for the diagnostic, and we want to avoid logging spam if the SDK
// ever calls getItem/setItem for other keys.
const SESSION_KEY = 'trybeup_session'

export const durableSessionStorage: SupportedStorage = {
  async getItem(key: string): Promise<string | null> {
    let local: string | null = null
    let localThrew = false
    try {
      local = localStorage.getItem(key)
    } catch {
      localThrew = true
    }
    if (local !== null) {
      if (key === SESSION_KEY) writeTombstone(local, 'read')
      // Lazy backfill into IDB. Fire-and-forget so we never block the
      // SDK's read path — the SDK only needs the value, not the
      // mirror's success.
      idbSet(key, local).then((outcome) => {
        if (key === SESSION_KEY && outcome !== 'ok') {
          // Quiet on success — we don't want to spam the log on every
          // page load. Only the failure case carries diagnostic weight.
          logAuthEvent('durable_session_write', {
            idb_outcome: outcome,
            source: 'lazy-backfill-on-read',
          })
        }
      }).catch(() => { /* ignore */ })
      return local
    }
    // localStorage empty. Could be (a) a true first-time visit, or
    // (b) Safari just wiped storage on a returning user. Read IDB to
    // tell the two apart — and, if (b), restore localStorage from the
    // IDB copy so the rest of the app behaves normally.
    const fromIdb = await idbGet(key)
    if (key === SESSION_KEY) {
      // Always log the localStorage-empty getItem path — it tells us
      // whether the IDB mirror was actually doing its job. If "ls was
      // empty and idb was also empty" shows up in the log between a
      // sign-in and a sign-out, that's the smoking gun: the mirror was
      // never written OR Safari wiped it too.
      logAuthEvent('durable_session_restore', {
        outcome: fromIdb !== null ? 'restored-from-idb' : 'both-empty',
        local_threw: localThrew,
      })
    }
    if (fromIdb !== null) {
      if (key === SESSION_KEY) writeTombstone(fromIdb, 'read')
      try {
        localStorage.setItem(key, fromIdb)
      } catch {
        /* ignore — IDB still authoritative for subsequent reads */
      }
    }
    return fromIdb
  },

  async setItem(key: string, value: string): Promise<void> {
    let localWriteOk = true
    try {
      localStorage.setItem(key, value)
    } catch {
      localWriteOk = false
    }
    if (key === SESSION_KEY) writeTombstone(value, 'write')
    // Awaited intentionally. Without this await, a Safari memory-
    // pressure evict landing in the brief window between localStorage
    // write and IDB flush could leave us with neither — defeating the
    // point of the mirror. The added ~5-30ms per sign-in / refresh is
    // negligible compared to the user impact of losing the session.
    const idbOutcome = await idbSet(key, value)
    if (key === SESSION_KEY) {
      // Always log the session write — there are only a handful per
      // session lifecycle (sign-in + each refresh ~every 50min), and
      // knowing whether the IDB mirror succeeded is exactly the data
      // we need to interpret a later /login bounce.
      logAuthEvent('durable_session_write', {
        local_ok: localWriteOk,
        idb_outcome: idbOutcome,
      })
    }
  },

  async removeItem(key: string): Promise<void> {
    try {
      localStorage.removeItem(key)
    } catch {
      /* ignore */
    }
    const outcome = await idbRemove(key)
    if (key === SESSION_KEY) {
      // Removing the session is always notable — it means either the
      // user signed out, a token refresh rejected, or the SDK cleared
      // a stale session. Pairs with the markSignoutSelfInitiated flag
      // on the consumer side.
      logAuthEvent('durable_session_remove', {
        idb_outcome: outcome,
      })
    }
  },
}
